import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {Row, Col, Card, Table, Spin, Button, Checkbox, message, Select, Tooltip, Typography, Space, Tag, Dropdown, Radio, Alert} from 'antd';
import {
    QuestionCircleOutlined,
    CheckCircleOutlined,
    PrinterOutlined,
    EditOutlined,
    LoadingOutlined,
    EyeOutlined,
    AppstoreOutlined,
    SwapOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { useCurrentDsp } from '../../../hooks/useCurrentDsp';
import SplitOrderWaybillPoolDrawer from './components/SplitOrderWaybillPoolDrawer';
import SplitOrderPublishConfirmModal from './components/SplitOrderPublishConfirmModal';
import SplitOrderPublishAllModal from './components/SplitOrderPublishAllModal';
import DataStaleModal from './components/DataStaleModal';
import RepublishConflictModal from './components/RepublishConflictModal';
import SortingTagScanUnpublishableModal from './components/SortingTagScanUnpublishableModal';
import SplitRouteDrawer, { SplitRouteRecord } from './components/SplitRouteDrawer';
import RoutePlanningTemplate from './components/RoutePlanningTemplate';
import SplitRouteHistoryDrawer from './components/SplitRouteHistoryDrawer';
import PrintTaskDateModal from './components/PrintTaskDateModal';
import { saveSplitRouteCache, loadSplitRouteCache, deleteSplitRouteCache, batchLoadSplitRouteCache, buildCacheFromApiChildren } from './utils/splitRouteCacheUtil';
import { resolveAvailableModes, isModeAllowed } from './utils/splitModeConfig';
import {
    resolveWarehouseNow,
    isTaskDateSelectable,
} from './utils/taskDateRule';
import {
    GET_DSP_TASK_DETAIL_WITH_CHILDREN,
    GetDspTaskDetailWithChildrenResponse,
    GetDspTaskDetailWithChildrenVariables,
} from './graphql/getDspTaskDetailWithChildren';
import {
    GET_ROUTE_ORDERS,
    GetRouteOrdersResponse,
    GetRouteOrdersVariables,
} from './graphql/getRouteOrders';
import {
    FAST_ROUTE_PLANNING,
    FAST_ROUTE_PLANNING_FOR_DSP,
    FastRoutePlanningResponse,
    FastRoutePlanningVariables,
} from './graphql/fastRoutePlanning';
import {
    GET_WAREHOUSE_PLANNING_ENTRY_CONFIG_BY_WAREHOUSE_ID,
    GetWarehousePlanningEntryConfigByWarehouseIdResponse,
    GetWarehousePlanningEntryConfigByWarehouseIdVariables,
} from './graphql/getWarehousePlanningEntryConfigByWarehouseId';
import {
    GET_PENDING_ROUTE_AGGREGATE_SUMMARY,
    GET_PENDING_ROUTE_AGGREGATE_SUMMARY_FOR_DSP,
    GetPendingRouteAggregateSummaryResponse,
    GetPendingRouteAggregateSummaryVariables,
} from './graphql/routePlanningOrderPool';
import {
    GET_PENDING_ROUTES,
    GetPendingRoutesResponse,
    GetPendingRoutesVariables,
    PendingRouteItem,
    GET_PENDING_ROUTES_FOR_DSP,
} from './graphql/getPendingRoutes';
import {
    ASSIGN_DRIVERS,
    AssignDriversResponse,
    AssignDriversVariables,
    AssignTaskInput,
    AssignType,
} from './graphql/assignDrivers';
import {
    FIND_DRIVERS_BY_DSP_IDS,
    FindDriversByDspIdsResponse,
    FindDriversByDspIdsVariables,
    DriverType,
} from './graphql/deliveryRoutes';
import {
    CHECK_DATA_STALE_BY_PLANNING_POOL,
    CheckDataStaleByPlanningPoolResponse,
    CheckDataStaleByPlanningPoolVariables,
    DataStaleCheckResult,
} from './graphql/checkDataStaleByPlanningPool';
import {
    CHECK_REPUBLISH_CONFLICT,
    CheckRepublishConflictResponse,
    CheckRepublishConflictVariables,
    ScannedOrderInfo,
} from './graphql/checkRepublishConflict';
import {
    GROUP_DSP_TASK_IDS_BY_STATUS,
    GroupDspTaskIdsByStatusResponse,
    GroupDspTaskIdsByStatusVariables,
} from './graphql/groupDspTaskIdsByStatus';
import {
    LIST_SORTING_TAG_SCAN_UNPUBLISHABLE_AREA_IDS,
    ListSortingTagScanUnpublishableAreaIdsResponse,
    ListSortingTagScanUnpublishableAreaIdsVariables,
} from './graphql/listSortingTagScanUnpublishableAreaIds';
import { GENERATE_ROUTE_PLANNING_BILL_OF_LADING_PDF, GENERATE_ROUTE_PLANNING_BILL_OF_LADING_PDF_FOR_DSP } from './graphql/generateRoutePlanningBillOfLadingPdf';
import {
    BATCH_IS_AUTO_ROUTE_GRAY,
    BatchIsAutoRouteGrayResponse,
    BatchIsAutoRouteGrayVariables,
} from './graphql/template/batchIsAutoRouteGray';
import {
    CHANGE_AUTO_ROUTE_DRIVER,
    ChangeAutoRouteDriverResponse,
    ChangeAutoRouteDriverVariables,
} from './graphql/template/changeAutoRouteDriver';

const { Text } = Typography;

/**
 * 区域发布状态
 */
enum RowStatus {
    PENDING = 'pending',               // 待发布：routeStatus=10
    PUBLISHING = 'publishing',         // 发布中：routeStatus=20
    PUBLISHED = 'published',           // 已发布：routeStatus=30
    PUBLISH_FAILED = 'publishFailed',  // 发布失败：routeStatus=40
    EDITED = 'edited',                 // 已编辑：已发布/发布失败后存在前端缓存
}

/**
 * 后端 routeStatus 枚举
 */
const ROUTE_STATUS = {
    PENDING: 10,
    PUBLISHING: 20,
    PUBLISHED: 30,
    PUBLISH_FAILED: 40,
} as const;

type SplitOrderPageMode = 'wms' | 'dsp';

interface SplitOrderPageProps {
    mode?: SplitOrderPageMode;
}

const SplitOrderPage: React.FC<SplitOrderPageProps> = ({ mode }) => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const isDsp = mode === 'dsp';
    const { dspId: currentDspId } = useCurrentDsp();
    const warehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;

    const { data: entryConfigData, refetch: refetchEntryConfig } = useQuery<
        GetWarehousePlanningEntryConfigByWarehouseIdResponse,
        GetWarehousePlanningEntryConfigByWarehouseIdVariables
    >(GET_WAREHOUSE_PLANNING_ENTRY_CONFIG_BY_WAREHOUSE_ID, {
        variables: { warehouseId: warehouseId! },
        skip: !warehouseId,
    });
    const availableModes = useMemo(
        () => resolveAvailableModes(entryConfigData?.config?.splitOrderConfig ?? []),
        [entryConfigData],
    );

    // ==================== State ====================
    // 页面不再展示任务日期（PRD R1）：发布 / 打印时在弹窗里由用户主动选择
    const warehouseNow = useMemo(
        () => resolveWarehouseNow(entryConfigData?.config?.warehouseCurrentTime),
        [entryConfigData],
    );
    // 仓库当天日期（不展示）：用于分拣贴号不可发布预判、拆分线路算法引导等无需用户选日期的地方
    const warehouseToday = useMemo(() => warehouseNow.startOf('day'), [warehouseNow]);
    // 本次发布在确认弹窗中选定的派送日期，确认前为 null
    const [publishTaskDate, setPublishTaskDate] = useState<Dayjs | null>(null);
    const [printModalVisible, setPrintModalVisible] = useState(false);

    // 取最新仓库时间（确认发布 / 打印时复核所选日期用），失败降级为已有时间
    const fetchFreshWarehouseNow = useCallback(async (): Promise<Dayjs> => {
        if (!warehouseId) return warehouseNow;
        try {
            const res = await refetchEntryConfig();
            return resolveWarehouseNow(res.data?.config?.warehouseCurrentTime ?? entryConfigData?.config?.warehouseCurrentTime);
        } catch {
            return warehouseNow;
        }
    }, [warehouseId, refetchEntryConfig, warehouseNow, entryConfigData]);
    const [driverAssignments, setDriverAssignments] = useState<
        Record<string, { driverId: number; driverName: string }>
    >({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [poolDrawerVisible, setPoolDrawerVisible] = useState(false);
    const [poolDrawerTab, setPoolDrawerTab] = useState<'all' | 'new' | 'returned'>('all');
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [publishModalVisible, setPublishModalVisible] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [dataStaleVisible, setDataStaleVisible] = useState(false);
    const [dataStaleResult, setDataStaleResult] = useState<DataStaleCheckResult | null>(null);
    const [republishConflictVisible, setRepublishConflictVisible] = useState(false);
    const [republishConflictOrders, setRepublishConflictOrders] = useState<ScannedOrderInfo[]>([]);
    const [unpublishableAreaIds, setUnpublishableAreaIds] = useState<number[]>([]);
    const [unpublishableModalVisible, setUnpublishableModalVisible] = useState(false);
    const [unpublishableModalAreaNames, setUnpublishableModalAreaNames] = useState<string[]>([]);
    const pendingPublishUnpublishableAreaIdsRef = useRef<number[]>([]);
    const [splitRouteDrawerVisible, setSplitRouteDrawerVisible] = useState(false);
    const [splitRouteRecord, setSplitRouteRecord] = useState<SplitRouteRecord | null>(null);
    const [templateDrawerVisible, setTemplateDrawerVisible] = useState(false);
    const [templateRecord, setTemplateRecord] = useState<SplitRouteRecord | null>(null);
    const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
    const [historyDspTaskId, setHistoryDspTaskId] = useState<number | null>(null);
    const [historyAreaId, setHistoryAreaId] = useState<number | null>(null);
    // 分享链接打开历史抽屉时的 warehouseId（deep link 用 URL 的，覆盖当前仓库上下文）
    const [historyWarehouseId, setHistoryWarehouseId] = useState<number | undefined>(undefined);
    const [searchParams, setSearchParams] = useSearchParams();
    const historyDeepLinkHandledRef = useRef(false);

    // 打开历史抽屉：设置 state 并把定位参数写入 URL（便于复制链接分享）
    const openHistoryDrawer = useCallback((dspTaskId: number | null, areaId: number | null, whId?: number) => {
        setHistoryDspTaskId(dspTaskId);
        setHistoryAreaId(areaId);
        setHistoryWarehouseId(whId);
        setHistoryDrawerVisible(true);
        const params = new URLSearchParams(searchParams);
        if (dspTaskId != null) params.set('historyDspTaskId', String(dspTaskId)); else params.delete('historyDspTaskId');
        if (areaId != null) params.set('historyAreaId', String(areaId)); else params.delete('historyAreaId');
        if (whId != null) params.set('historyWarehouseId', String(whId)); else params.delete('historyWarehouseId');
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // 关闭历史抽屉：清掉 URL 上的定位参数，避免刷新又弹
    const closeHistoryDrawer = useCallback(() => {
        setHistoryDrawerVisible(false);
        const params = new URLSearchParams(searchParams);
        params.delete('historyDspTaskId');
        params.delete('historyAreaId');
        params.delete('historyWarehouseId');
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // 分享链接：URL 带 historyDspTaskId 时自动打开历史抽屉（仅处理一次）
    useEffect(() => {
        if (historyDeepLinkHandledRef.current) return;
        const dspTaskIdStr = searchParams.get('historyDspTaskId');
        if (!dspTaskIdStr) return;
        historyDeepLinkHandledRef.current = true;
        const dspTaskId = Number(dspTaskIdStr);
        if (!Number.isFinite(dspTaskId) || dspTaskId <= 0) return;
        setHistoryDspTaskId(dspTaskId);
        setHistoryAreaId(Number(searchParams.get('historyAreaId')) || null);
        setHistoryWarehouseId(Number(searchParams.get('historyWarehouseId')) || undefined);
        setHistoryDrawerVisible(true);
    }, [searchParams]);
    const [splitConfigs, setSplitConfigs] = useState<Record<string, any>>({});
    const [subRowDrivers, setSubRowDrivers] = useState<Record<string, string>>({});
    const [grayDriverDropdownVisible, setGrayDriverDropdownVisible] = useState<Record<string, boolean>>({});
    const [grayDriverSearchText, setGrayDriverSearchText] = useState<Record<string, string>>({});
    const publishFromDrawerRef = useRef(false);

    // ==================== Queries & Mutations ====================
    const summaryVariables: GetPendingRouteAggregateSummaryVariables | undefined = useMemo(() => {
        if (!warehouseId) return undefined;
        return {
            request: {
                warehouseId,
                dspId: isDsp && currentDspId != null ? Number(currentDspId) : undefined,
            },
        };
    }, [warehouseId, isDsp, currentDspId]);

    const summaryQuery = mode === 'wms' ? GET_PENDING_ROUTE_AGGREGATE_SUMMARY : GET_PENDING_ROUTE_AGGREGATE_SUMMARY_FOR_DSP;
    const summaryKey = mode === 'wms' ? 'getPendingRouteAggregateSummary' : 'getPendingRouteAggregateSummaryForDsp';

    const { data: summaryRawData, loading: summaryLoading, refetch: refetchSummary } = useQuery(summaryQuery, {
        variables: summaryVariables!,
        skip: !summaryVariables,
        fetchPolicy: 'network-only',
    });
    const summaryData = useMemo(() => ({
        getPendingRouteAggregateSummary: (summaryRawData as any)?.[summaryKey] ?? null
    }), [summaryRawData, summaryKey]);

    const pendingRoutesQuery = mode === 'wms' ? GET_PENDING_ROUTES : GET_PENDING_ROUTES_FOR_DSP;
    const pendingRoutesKey = mode === 'wms' ? 'getPendingRoutes' : 'getPendingRoutesForDsp';

    const { data: pendingRoutesRawData, loading: pendingRoutesLoading, refetch: refetchPendingRoutes } = useQuery(pendingRoutesQuery, {
        variables: summaryVariables!,
        skip: !summaryVariables,
        fetchPolicy: 'network-only',
    });
    const pendingRoutesData = useMemo(() => {
        const routes = (pendingRoutesRawData as any)?.[pendingRoutesKey] ?? [];
        return { getPendingRoutes: routes as PendingRouteItem[] };
    }, [pendingRoutesRawData, pendingRoutesKey]);

    const { data: driversData, loading: driversLoading } = useQuery<
        FindDriversByDspIdsResponse,
        FindDriversByDspIdsVariables
    >(FIND_DRIVERS_BY_DSP_IDS, {
        variables: { dspIds: useMemo(() => {
            const ids = new Set<number>();
            (pendingRoutesData?.getPendingRoutes ?? []).forEach((r) => { if (r.dspId) ids.add(r.dspId); });
            return Array.from(ids);
        }, [pendingRoutesData]) },
        skip: !pendingRoutesData?.getPendingRoutes?.length,
    });

    const [assignDrivers] = useMutation<AssignDriversResponse, AssignDriversVariables>(ASSIGN_DRIVERS);
    const [isPublishing, setIsPublishing] = useState(false);
    const [checkDataStale] = useLazyQuery<CheckDataStaleByPlanningPoolResponse, CheckDataStaleByPlanningPoolVariables>(
        CHECK_DATA_STALE_BY_PLANNING_POOL, { fetchPolicy: 'network-only' }
    );
    const [checkRepublishConflict, { loading: republishChecking }] = useLazyQuery<
        CheckRepublishConflictResponse, CheckRepublishConflictVariables
    >(CHECK_REPUBLISH_CONFLICT, { fetchPolicy: 'network-only' });

    const [fetchDspTaskIdsByStatus] = useLazyQuery<
        GroupDspTaskIdsByStatusResponse,
        GroupDspTaskIdsByStatusVariables
    >(GROUP_DSP_TASK_IDS_BY_STATUS, { fetchPolicy: 'network-only' });

    const [fetchUnpublishableAreaIds] = useLazyQuery<
        ListSortingTagScanUnpublishableAreaIdsResponse,
        ListSortingTagScanUnpublishableAreaIdsVariables
    >(LIST_SORTING_TAG_SCAN_UNPUBLISHABLE_AREA_IDS, { fetchPolicy: 'network-only' });

    const [fetchTaskDetail] = useLazyQuery<
        GetDspTaskDetailWithChildrenResponse,
        GetDspTaskDetailWithChildrenVariables
    >(GET_DSP_TASK_DETAIL_WITH_CHILDREN, { fetchPolicy: 'network-only' });

    const [fetchRouteOrders] = useLazyQuery<
        GetRouteOrdersResponse,
        GetRouteOrdersVariables
    >(GET_ROUTE_ORDERS, { fetchPolicy: 'network-only' });

    const fastRoutePlanningQuery = mode === 'wms' ? FAST_ROUTE_PLANNING : FAST_ROUTE_PLANNING_FOR_DSP;
    const fastRoutePlanningKey = mode === 'wms' ? 'fastRoutePlanning' : 'fastRoutePlanningForDsp';
    const [fetchFastRoutePlanning] = useLazyQuery(fastRoutePlanningQuery, { fetchPolicy: 'network-only' });

    const billOfLadingMutation = mode === 'wms' ? GENERATE_ROUTE_PLANNING_BILL_OF_LADING_PDF : GENERATE_ROUTE_PLANNING_BILL_OF_LADING_PDF_FOR_DSP;
    const billOfLadingKey = mode === 'wms' ? 'generateRoutePlanningBillOfLadingPdf' : 'generateRoutePlanningBillOfLadingPdfForDsp';
    const [generateBillOfLadingPdf] = useMutation(billOfLadingMutation);
    const [changeAutoRouteDriver] = useMutation<ChangeAutoRouteDriverResponse, ChangeAutoRouteDriverVariables>(CHANGE_AUTO_ROUTE_DRIVER);
    const [printLoading, setPrintLoading] = useState(false);

    const prevInProgressRef = useRef<Set<number>>(new Set());

    // ==================== Derived Data ====================
    const summary = summaryData?.getPendingRouteAggregateSummary;
    const pendingPlanning = summary?.pendingPlanningParcelCount ?? 0;
    const newToWarehouse = summary?.newToWarehouseParcelCount ?? 0;
    const returnRedispatch = summary?.returnRedispatchParcelCount ?? 0;
    const published = summary?.publishedParcelCount ?? 0;

    const driversByDspId = useMemo<Record<number, DriverType[]>>(() => {
        if (!driversData?.findDriversByDspIds) return {};
        const grouped: Record<number, DriverType[]> = {};
        driversData.findDriversByDspIds.forEach((driver) => {
            if (!grouped[driver.dspId]) grouped[driver.dspId] = [];
            grouped[driver.dspId].push(driver);
        });
        return grouped;
    }, [driversData]);

    const assignTypeToSplitMode = (at: number): string => {
        if (at === AssignType.BY_MAP) return 'map';
        if (at === AssignType.BY_ZIPCODE) return 'zipcode';
        if (at === AssignType.BY_STOP) return 'stop';
        if (at === AssignType.BY_ALGORITHM) return 'algo';
        if (at === AssignType.DIRECT) return 'direct';
        return 'direct';
    };

    const tableData = useMemo(() => {
        const routes = pendingRoutesData?.getPendingRoutes ?? [];
        const result: any[] = [];

        // 通用：解析子行司机（subRowDrivers 覆盖 > 原始值）
        const resolveSegDriver = (segId: string, originalAccountId: string | undefined, dspId: number, clearBlacklisted = false) => {
            const overrideAccountId = segId in subRowDrivers ? subRowDrivers[segId] : undefined;
            const driverAccountId = overrideAccountId !== undefined ? (overrideAccountId || undefined) : originalAccountId;
            const resolvedDriver = driverAccountId
                ? (dspId ? driversByDspId[dspId] ?? [] : []).find((d: any) => d.accountId.toString() === driverAccountId)
                : undefined;
            // 缓存场景：黑名单/离职/未实名司机清空
            if (clearBlacklisted && resolvedDriver && (resolvedDriver.isBlacklist || !resolvedDriver.isEmployed || resolvedDriver.verificationStatus !== 2)) {
                return { driverAccountId: undefined, driverId: undefined, driverName: undefined };
            }
            return {
                driverAccountId,
                driverId: resolvedDriver ? Number(resolvedDriver.accountId) : undefined,
                driverName: resolvedDriver ? resolvedDriver.name : undefined,
            };
        };

        routes.forEach((route: PendingRouteItem) => {
            const key = `${route.areaId}-${route.dspTaskId ?? 0}`;
            const localDriver = driverAssignments[key];
            const config = route.areaId ? splitConfigs[route.areaId] : undefined;
            const isPublished = Number(route.dspTaskId) !== 0;
            // 状态判定：优先使用 routeStatus；若为"已发布/发布失败"且存在前端缓存，则显示为"发布后调整"
            const rs = route.routeStatus;
            let status: RowStatus;
            if (rs === ROUTE_STATUS.PENDING) {
                status = RowStatus.PENDING;
            } else if (rs === ROUTE_STATUS.PUBLISHING) {
                status = RowStatus.PUBLISHING;
            } else if (rs === ROUTE_STATUS.PUBLISHED) {
                status = config ? RowStatus.EDITED : RowStatus.PUBLISHED;
            } else if (rs === ROUTE_STATUS.PUBLISH_FAILED) {
                status = config ? RowStatus.EDITED : RowStatus.PUBLISH_FAILED;
            } else {
                // 兜底：后端未返回 routeStatus 时回退旧逻辑
                status = !isPublished
                    ? RowStatus.PENDING
                    : config
                        ? RowStatus.EDITED
                        : RowStatus.PUBLISHED;
            }

            // 基础行数据（后端）
            const row: any = {
                key,
                isSegment: false,
                status,
                routeStatus: rs,
                dspTaskId: route.dspTaskId,
                driverTaskId: route.driverTaskId,
                areaId: route.areaId,
                areaName: route.areaName,
                dspId: route.dspId,
                dspName: route.dspName,
                newParcels: route.newParcelCount ?? 0,
                plannedParcels: route.plannedParcelCount ?? 0,
                totalParcels: route.totalParcelCount ?? 0,
                driverName: localDriver?.driverName ?? route.driverName,
                driverId: localDriver?.driverId ?? (Number(route.driverId) || undefined),
                driverAccountId: localDriver?.driverId ?? route.driverId,
                split: false,
                parcelNumberStart: route.parcelNumberStart,
                parcelNumberEnd: route.parcelNumberEnd,
                childTaskList: [],
            };

            // totalParcels 为 0 直接展示，不处理子行
            if (row.totalParcels === 0) {
                result.push(row);
                return;
            }

            // ===== 第一优先级：缓存 =====
            if (config) {
                const { lastSplitMode } = config;
                const segments: any[] = lastSplitMode === 'map' ? config.map
                    : lastSplitMode === 'zipcode' ? config.zipcode
                    : lastSplitMode === 'stop' ? config.stop
                    : lastSplitMode === 'algo' ? (config.algo?.segments ?? [])
                    : [];
                const validSegs = (segments ?? []).filter((seg: any) => seg.assignedOrderIds?.length > 0 || seg.totalParcelCount > 0);

                if (validSegs.length > 1) {
                    // 多分段：拆分
                    row.split = true;
                    result.push(row);
                    validSegs.forEach((seg: any) => {
                        const { driverAccountId, driverId, driverName } = resolveSegDriver(seg.id, seg.driverAccountId, row.dspId, true);
                        result.push({
                            key: `${row.key}-seg-${seg.id}`,
                            isSegment: true,
                            status,
                            splitMode: lastSplitMode,
                            parentRecord: row,
                            dspId: row.dspId,
                            dspName: row.dspName,
                            ...seg,
                            driverAccountId,
                            driverId,
                            driverName,
                        });
                    });
                } else {
                    // 0 或 1 个分段：当直接指派，缓存司机/起终点/模式作为兜底
                    if (validSegs.length === 1) {
                        const seg = validSegs[0];
                        if (seg.driverAccountId && !localDriver) {
                            const driver = (row.dspId ? driversByDspId[row.dspId] ?? [] : [])
                                .find((d: any) => d.accountId.toString() === seg.driverAccountId);
                            if (driver && !driver.isBlacklist) {
                                row.driverId = Number(driver.accountId);
                                row.driverName = driver.name;
                            }
                        }
                        // 剥离 GraphQL 查询结果自动携带的 __typename，避免回填后作为 DriverAddressInput 发布时校验失败
                        row.startAddress = seg.startAddress ? (({ __typename, ...r }) => r)(seg.startAddress as any) : undefined;
                        row.endAddress = seg.endAddress ? (({ __typename, ...r }) => r)(seg.endAddress as any) : undefined;
                        row.splitMode = lastSplitMode;
                    }
                    result.push(row);
                }
                return;
            }

            // ===== 第二优先级：后端 API 数据 =====
            const apiChildren = route.childTaskList ?? [];
            const apiSplitMode = apiChildren.length > 0
                ? assignTypeToSplitMode(apiChildren[0].assignType ?? AssignType.BY_MAP)
                : 'direct';
            const apiModeAllowed = isModeAllowed(apiSplitMode, availableModes);
            // 已发布且为直接指派：允许在主页面直接改司机
            row.isDirectPublished = isPublished && apiSplitMode === 'direct';
            if (isPublished && apiChildren.length > 1 && apiModeAllowed) {
                // 多子任务：展开子行
                row.split = true;
                result.push(row);
                apiChildren.forEach((child: any, idx: number) => {
                    const segId = `${route.areaId}-${child.dspTaskId ?? idx}`;
                    const resolved = resolveSegDriver(
                        segId, child.driverId ? String(child.driverId) : undefined, row.dspId
                    );
                    result.push({
                        key: `${row.key}-seg-${child.dspTaskId ?? idx}`,
                        isSegment: true,
                        status,
                        fromApi: true,
                        splitMode: assignTypeToSplitMode(child.assignType ?? AssignType.BY_MAP),
                        parentRecord: row,
                        id: segId,
                        name: child.areaName,
                        dspId: child.dspId ?? row.dspId,
                        dspName: child.dspName ?? row.dspName,
                        dspTaskId: child.dspTaskId,
                        driverTaskId: child.driverTaskId,
                        driverAccountId: resolved.driverAccountId,
                        driverId: resolved.driverId ?? child.driverId,
                        driverName: resolved.driverName ?? child.driverName,
                        totalParcels: child.totalParcelCount ?? 0,
                        parcelNumberStart: child.parcelNumberStart ?? null,
                        parcelNumberEnd: child.parcelNumberEnd ?? null,
                        assignedOrderIds: (child.routePlanningOrderList ?? []).map((o: any) => o.orderId).filter(Boolean),
                        assignTypeDetail: child.assignTypeDetail,
                    });
                });
                return;
            }

            // ===== 已发布单子任务：回显 child 司机到父行 =====
            if (isPublished && apiChildren.length === 1) {
                const child = apiChildren[0];
                if (child.driverId && !row.driverId) {
                    const driver = (row.dspId ? driversByDspId[row.dspId] ?? [] : [])
                        .find((d: any) => d.accountId.toString() === String(child.driverId));
                    if (driver) {
                        row.driverId = Number(driver.accountId);
                        row.driverName = driver.name;
                    }
                }
            }

            // ===== 直接展示父行（无缓存未发布 / 已发布无子任务或单子任务） =====
            result.push(row);
        });

        return result;
    }, [pendingRoutesData, driverAssignments, splitConfigs, driversByDspId, subRowDrivers, availableModes]);


    const areaNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        tableData.forEach((row: any) => { if (row.areaId && row.areaName) map[row.areaId] = row.areaName; });
        return map;
    }, [tableData]);

    const dspNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        tableData.forEach((row: any) => { if (row.dspId && row.dspName) map[row.dspId] = row.dspName; });
        return map;
    }, [tableData]);

    const renderSplitInfo = (seg: any): React.ReactNode => {
        const parcelCount = (seg.assignedOrderIds?.length ?? 0) > 0
            ? seg.assignedOrderIds.length
            : (seg.totalParcels ?? seg.totalParcelCount ?? 0);

        // API 来的分段子行有编号范围
        const numberRange = (seg.fromApi && seg.parcelNumberStart != null && seg.parcelNumberEnd != null)
            ? ` Stop ${seg.parcelNumberStart}-${seg.parcelNumberEnd}`
            : '';

        if (seg.splitMode === 'map') {
            return <span style={{ color: '#4f46e5' }}>{t('splitOrder.page.mapSegment', { name: seg.name, count: parcelCount })}{numberRange}</span>;
        }
        if (seg.splitMode === 'zipcode') {
            if (seg.zipcodes?.length > 0) {
                const zipStr = (seg.zipcodes ?? []).join(' ');
                return <span style={{ color: '#4f46e5' }}>{t('splitOrder.page.zipcodeSegment', { zipcodes: zipStr, count: parcelCount })}{numberRange}</span>;
            }
            return <span style={{ color: '#4f46e5' }}>{t('splitOrder.page.zipcodeSegmentNoZip', { count: parcelCount })}{numberRange}</span>;
        }
        if (seg.splitMode === 'stop') {
            // 优先用 assignTypeDetail（API 数据，如 ["47-88","100-185"]）
            if (seg.assignTypeDetail?.length > 0) {
                const rangeStr = 'Stop ' + seg.assignTypeDetail.map((d: string) => d.replace(',', '-')).join(' ');
                return (
                    <span style={{ color: '#4f46e5' }}>
                        {rangeStr}
                        <span style={{ marginLeft: 4 }}>（{parcelCount}）</span>
                    </span>
                );
            }
            // localStorage 缓存数据用 stopRanges
            if (seg.stopRanges?.length > 0) {
                const rangeStr = 'Stop ' + seg.stopRanges.map((r: any) => `${r.parcelNumberStart}-${r.parcelNumberEnd}`).join(' ');
                return (
                    <span style={{ color: '#4f46e5' }}>
                        {rangeStr}
                        <span style={{ marginLeft: 4 }}>（{parcelCount}）</span>
                    </span>
                );
            }
            return <span style={{ color: '#4f46e5' }}>{t('splitOrder.page.stopSegment', { count: parcelCount })}</span>;
        }
        if (seg.splitMode === 'algo') {
            // 从 assignTypeDetail 或 seg 直接取 zipcodes
            let zipcodes: string[] = seg.preferredZipcodes ?? [];
            if (zipcodes.length === 0 && seg.assignTypeDetail?.length > 0) {
                const first = seg.assignTypeDetail[0];
                zipcodes = first && /^\d+,\d+$/.test(first) ? seg.assignTypeDetail.slice(1) : seg.assignTypeDetail;
            }
            const zipcodeStr = zipcodes.join(', ');
            const rangeStr = seg.parcelNumberStart != null && seg.parcelNumberEnd != null
                ? `Stop ${seg.parcelNumberStart}-${seg.parcelNumberEnd} ` : '';
            return (
                <span style={{ color: '#4f46e5' }}>
                    {rangeStr}（{parcelCount}）{zipcodeStr && <span style={{ marginLeft: 4, color: '#6b7280' }}>{zipcodeStr}</span>}
                </span>
            );
        }
        if (seg.splitMode === 'direct') {
            return <span style={{ color: '#4f46e5' }}>{t('splitOrder.page.directAssign', { count: parcelCount })}{numberRange}</span>;
        }
        return <span style={{ color: '#4f46e5' }}>（{parcelCount}）</span>;
    };

    const dspTaskIdsForPlanningPoll = useMemo(() => {
        const ids = new Set<number>();
        const addId = (raw: unknown) => {
            if (raw == null || raw === '') return;
            const n = Number(raw);
            if (!Number.isFinite(n) || n === 0) return;
            ids.add(n);
        };
        // 只关注发布中（routeStatus=20）的任务：父任务 dspTaskId 以及其拆分后的子任务 dspTaskId 都要加入轮询
        // （后端按 is_split=0 过滤，拆分父任务不会返回结果，子任务才是真正能拿到成功/失败状态的叶子）
        const routes = pendingRoutesData?.getPendingRoutes ?? [];
        routes.forEach((route: PendingRouteItem) => {
            if (route.routeStatus !== ROUTE_STATUS.PUBLISHING) return;
            addId(route.dspTaskId);
            (route.childTaskList ?? []).forEach((child) => addId(child.dspTaskId));
        });
        return Array.from(ids);
    }, [pendingRoutesData]);

    const dspTaskIdsPollKey = useMemo(
        () => [...dspTaskIdsForPlanningPoll].sort((a, b) => a - b).join(','),
        [dspTaskIdsForPlanningPoll],
    );

    // dspTaskId -> 展示名称（父行 = areaName；子行 = child.areaName，通常形如 "父区域-子段"）
    const dspTaskIdNameMap = useMemo<Record<number, string>>(() => {
        const map: Record<number, string> = {};
        const routes = pendingRoutesData?.getPendingRoutes ?? [];
        routes.forEach((route: PendingRouteItem) => {
            if (route.dspTaskId) map[Number(route.dspTaskId)] = route.areaName || String(route.dspTaskId);
            (route.childTaskList ?? []).forEach((child: any) => {
                if (child.dspTaskId) map[Number(child.dspTaskId)] = child.areaName || String(child.dspTaskId);
            });
        });
        return map;
    }, [pendingRoutesData]);

    // 轮询结束时根据成功/失败列表展示提示
    const showPublishResultToast = useCallback((
        successIds: number[],
        failedIds: number[],
    ) => {
        const successNames = successIds.map(id => dspTaskIdNameMap[id]).filter(Boolean);
        const failedNames = failedIds.map(id => dspTaskIdNameMap[id]).filter(Boolean);

        if (successNames.length > 0) {
            message.success(t('delivery.routes.message.publishSucceededAreas', {
                areas: successNames.join('、'),
                defaultValue: `排线发布成功：${successNames.join('、')}`,
            }));
        }
        if (failedNames.length > 0) {
            message.error(t('delivery.routes.message.publishFailedAreas', {
                areas: failedNames.join('、'),
                defaultValue: `排线发布失败：${failedNames.join('、')}`,
            }));
        }
    }, [dspTaskIdNameMap, t]);

    useEffect(() => {
        prevInProgressRef.current = new Set(dspTaskIdsForPlanningPoll);

        if (dspTaskIdsForPlanningPoll.length === 0) {
            return;
        }

        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval> | null = null;

        const poll = async () => {
            try {
                const res = await fetchDspTaskIdsByStatus({
                    variables: { dspTaskIds: dspTaskIdsForPlanningPoll },
                });
                if (cancelled) return;
                const grouped = res.data?.groupDspTaskIdsByStatus;
                const inProgress = new Set(grouped?.inProgressDspTaskIds ?? []);
                const publishSuccess = grouped?.publishSuccessDspTaskIds ?? [];
                const publishFailed = grouped?.publishFailedDspTaskIds ?? [];

                // 本轮新完成 = 上轮 inProgress 中、本轮不再 inProgress 的任务
                const newlyDone = Array.from(prevInProgressRef.current).filter((id) => !inProgress.has(id));
                if (newlyDone.length > 0) {
                    const newSuccess = publishSuccess.filter((id) => newlyDone.includes(id));
                    const newFailed = publishFailed.filter((id) => newlyDone.includes(id));
                    showPublishResultToast(newSuccess, newFailed);
                    await refetchPendingRoutes();
                    await refetchSummary();
                }
                prevInProgressRef.current = inProgress;

                if (inProgress.size === 0 && intervalId) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            } catch {
                // 单次失败忽略，下一轮继续
            }
        };

        void poll();
        intervalId = setInterval(poll, 10000);

        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [dspTaskIdsPollKey, dspTaskIdsForPlanningPoll, fetchDspTaskIdsByStatus, refetchPendingRoutes, refetchSummary, showPublishResultToast]);


    // 拉取分拣贴号灰度场景下"不可发布"的区域 ID 列表
    // 页面行级置灰按仓库当天日期预判；发布时再按弹窗所选日期复核
    const taskDateStrForUnpublishable = warehouseToday.format('YYYY-MM-DD');
    const visibleAreaIdsForUnpublishable = useMemo(() => {
        const ids = new Set<number>();
        (pendingRoutesData?.getPendingRoutes ?? []).forEach((r: PendingRouteItem) => {
            if (r.areaId) ids.add(Number(r.areaId));
        });
        return Array.from(ids);
    }, [pendingRoutesData]);
    const visibleAreaIdsKey = useMemo(
        () => [...visibleAreaIdsForUnpublishable].sort((a, b) => a - b).join(','),
        [visibleAreaIdsForUnpublishable],
    );
    useEffect(() => {
        if (!warehouseId || visibleAreaIdsForUnpublishable.length === 0) {
            setUnpublishableAreaIds([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchUnpublishableAreaIds({
                    variables: {
                        taskDate: taskDateStrForUnpublishable,
                        warehouseId,
                        areaIds: visibleAreaIdsForUnpublishable,
                    },
                });
                if (cancelled) return;
                const ids = res.data?.listSortingTagScanUnpublishableAreaIds ?? [];
                setUnpublishableAreaIds(ids.map((id) => Number(id)));
            } catch {
                if (!cancelled) setUnpublishableAreaIds([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [warehouseId, taskDateStrForUnpublishable, visibleAreaIdsKey, visibleAreaIdsForUnpublishable, fetchUnpublishableAreaIds]);

    const unpublishableAreaIdSet = useMemo(
        () => new Set(unpublishableAreaIds),
        [unpublishableAreaIds],
    );

    const { data: autoRouteGrayData, loading: autoRouteGrayLoading, error: autoRouteGrayError } = useQuery<
        BatchIsAutoRouteGrayResponse,
        BatchIsAutoRouteGrayVariables
    >(BATCH_IS_AUTO_ROUTE_GRAY, {
        variables: { warehouseId: warehouseId!, areaIds: visibleAreaIdsForUnpublishable },
        skip: !warehouseId || visibleAreaIdsForUnpublishable.length === 0,
        fetchPolicy: 'network-only',
    });
    // 灰度结果就绪判据：无区域需查询则视为就绪；否则必须等灰度查询结束（数据到位或出错兜底）。
    // 用于门控表格渲染，避免灰度区域先渲染出"拆分线路"按钮、灰度数据回来后才刷成"编辑模板"，中间窗口用户误点。
    // 出错时按就绪处理（降级为非灰度渲染，与改动前一致），避免表格永久 loading。
    const autoRouteGrayReady = visibleAreaIdsForUnpublishable.length === 0
        || (!autoRouteGrayLoading && (!!autoRouteGrayData || !!autoRouteGrayError));
    const autoRouteGrayAreaIdSet = useMemo(
        () => new Set(
            (autoRouteGrayData?.batchIsAutoRouteGray ?? [])
                .filter(r => r.gray)
                .map(r => Number(r.areaId)),
        ),
        [autoRouteGrayData],
    );

    const negativeDriverSummary = useMemo(() => {
        const rows = tableData.filter((r: any) => {
            if (r.driverId == null || Number(r.driverId) >= 0) return false;
            const checkAreaId = r.isSegment ? Number(r.parentRecord?.areaId) : Number(r.areaId);
            return autoRouteGrayAreaIdSet.has(checkAreaId);
        });
        if (rows.length === 0) return null;
        const areaNames = new Set<string>();
        rows.forEach((r: any) => {
            const name = r.isSegment ? r.parentRecord?.areaName : r.areaName;
            if (name) areaNames.add(name);
        });
        return { routes: Array.from(areaNames), count: rows.length };
    }, [tableData, autoRouteGrayAreaIdSet]);

    const refreshSplitConfigs = useCallback(async () => {
        if (!warehouseId) return;
        const routes = pendingRoutesData?.getPendingRoutes ?? [];
        const areaIds = routes.map((r: PendingRouteItem) => r.areaId).filter((id): id is number => id != null);
        if (areaIds.length === 0) { setSplitConfigs({}); return; }
        const cacheMap = await batchLoadSplitRouteCache(warehouseId, areaIds);
        const configs: Record<string, any> = {};
        for (const [areaId, parsed] of Object.entries(cacheMap)) {
            if (parsed?.lastSplitMode && isModeAllowed(parsed.lastSplitMode, availableModes)) {
                configs[areaId] = parsed;
            }
        }
        setSplitConfigs(configs);
    }, [warehouseId, pendingRoutesData, availableModes]);

    useEffect(() => { refreshSplitConfigs(); }, [refreshSplitConfigs]);

    const selectableKeys = useMemo(
        () => tableData
            .filter((r: any) =>
                !r.isSegment
                && r.totalParcels > 0
                && r.status !== RowStatus.PUBLISHING
                && !unpublishableAreaIdSet.has(Number(r.areaId))
                && !autoRouteGrayAreaIdSet.has(Number(r.areaId))
            )
            .map((r: any) => r.key),
        [tableData, unpublishableAreaIdSet, autoRouteGrayAreaIdSet],
    );

    // tableData 变化后清理 selectedRowKeys 中已不存在、已进入 PUBLISHING、或已变为不可发布的 key
    const allRowKeys = useMemo(() => new Set(tableData.map((r: any) => r.key)), [tableData]);
    const selectableKeysSet = useMemo(() => new Set(selectableKeys), [selectableKeys]);
    useEffect(() => {
        setSelectedRowKeys(prev => {
            const cleaned = prev.filter(k => allRowKeys.has(k) && selectableKeysSet.has(k));
            return cleaned.length === prev.length && cleaned.every((k, i) => k === prev[i]) ? prev : cleaned;
        });
    }, [allRowKeys, selectableKeysSet]);

    const isAllSelected = selectableKeys.length > 0 && selectedRowKeys.length === selectableKeys.length;
    const isIndeterminate = selectedRowKeys.length > 0 && selectedRowKeys.length < selectableKeys.length;

    // ==================== Callbacks ====================
    /**
     * 表格多选
     */
    const handleSelectAll = (checked: boolean) => {
        setSelectedRowKeys(checked ? selectableKeys : []);
    };

    /**
     * 表格单选
     */
    const handleRowSelect = (key: React.Key, checked: boolean) => {
        setSelectedRowKeys((prev) =>
            checked ? [...prev, key] : prev.filter((k) => k !== key),
        );
    };

    /**
     * 表格更换司机
     */
    const handleDriverChange = useCallback((rowKey: string, dspId: number, driverAccountId: string | undefined) => {
        if (!driverAccountId) {
            setDriverAssignments(prev => ({
                ...prev,
                [rowKey]: { driverId: 0, driverName: '' },
            }));
            return;
        }
        const allDrivers = driversByDspId[dspId] || [];
        const driver = allDrivers.find((d: any) => d.accountId.toString() === driverAccountId);
        if (driver) {
            setDriverAssignments(prev => ({
                ...prev,
                [rowKey]: { driverId: Number(driver.accountId), driverName: driver.name },
            }));
        }
    }, [driversByDspId]);

    /**
     * 打开规划池
     */
    const openPoolDrawer = (tab: 'all' | 'new' | 'returned') => {
        setPoolDrawerTab(tab);
        setPoolDrawerVisible(true);
    };

    /**
     * 校验发布分配合理性
     */
    const validateForPublish = useCallback(() => {
        const errors: string[] = [];

        // 任务日期改为在确认弹窗中选择并按仓库时间复核（见 handleConfirmPublish），这里不再校验

        // 校验是否有选中区域
        const selectedRows = tableData.filter(
            (row: any) => selectedRowKeys.includes(row.key) && row.status !== RowStatus.PUBLISHING,
        );
        if (selectedRows.length === 0) {
            errors.push(t('delivery.routes.message.noAreasSelected'));
            return errors;
        }

        // 校验每个选中区域
        selectedRows.forEach((row: any) => {
            if (row.totalParcels === 0) {
                errors.push(t('delivery.routes.message.areaHasNoParcels', { areaName: row.areaName }));
                return;
            }

            if (row.split) {
                // 分段模式：从 tableData 展开的子行取（已合并 subRowDrivers）
                const segmentRows = tableData.filter(
                    (r: any) => r.isSegment && r.parentRecord?.key === row.key
                );
                const totalAssigned = segmentRows.reduce((sum: number, seg: any) => {
                    return sum + (seg.totalParcels || seg.totalParcelCount || 0);
                }, 0);
                if (totalAssigned !== row.totalParcels) {
                    errors.push(t('splitOrder.page.segmentParcelMismatch', { areaName: row.areaName, assigned: totalAssigned, total: row.totalParcels }));
                }
                // 校验每个分段是否分配了司机
                const noDriverSegments = segmentRows.filter((seg: any) => !seg.driverId && !seg.driverAccountId);
                if (noDriverSegments.length > 0) {
                    errors.push(t('splitOrder.page.segmentNoDriver', { areaName: row.areaName, segments: noDriverSegments.map((s: any) => s.name || s.areaName).join(', ') }));
                }
                // stop 模式：校验各分段包裹数与编号范围是否一致（司机领走部分包裹后会不一致）
                segmentRows.forEach((seg: any) => {
                    if (seg.splitMode !== 'stop') return;
                    const segParcels = seg.totalParcels ?? seg.totalParcelCount ?? 0;
                    const rangeCount = (seg.parcelNumberEnd != null && seg.parcelNumberStart != null)
                        ? seg.parcelNumberEnd - seg.parcelNumberStart + 1
                        : null;
                    if (rangeCount != null && segParcels !== rangeCount) {
                        errors.push(t('splitOrder.page.segmentRangeMismatch', { areaName: row.areaName, segment: seg.name || seg.areaName, parcels: segParcels, start: seg.parcelNumberStart, end: seg.parcelNumberEnd, rangeCount }));
                    }
                });
            } else {
                // 未分段：校验选了司机
                if (!row.driverId) {
                    errors.push(t('delivery.routes.message.areaMustHaveDriver', { areaName: row.areaName }));
                }
                // 未分段 stop 模式：校验包裹数与编号范围是否一致
                if (row.splitMode === 'stop' && row.parcelNumberStart != null && row.parcelNumberEnd != null) {
                    const rangeCount = row.parcelNumberEnd - row.parcelNumberStart + 1;
                    if (row.totalParcels !== rangeCount) {
                        errors.push(t('splitOrder.page.areaRangeMismatch', { areaName: row.areaName, parcels: row.totalParcels, start: row.parcelNumberStart, end: row.parcelNumberEnd, rangeCount }));
                    }
                }
            }
        });

        return errors;
    }, [tableData, selectedRowKeys, t]);

    /**
     * 打印提货单（R4）：点「打印」先弹出选择任务日期的对话框
     */
    const handlePrintClick = useCallback(async () => {
        if (!warehouseId) {
            message.error(t('delivery.routes.message.pleaseSelectWarehouseAndDate'));
            return;
        }
        // 打开前刷新仓库时间，保证日期面板的可选范围准确
        await fetchFreshWarehouseNow();
        setPrintModalVisible(true);
    }, [warehouseId, fetchFreshWarehouseNow, t]);

    /**
     * 打印对话框点「确认」：按最新仓库时间复核所选日期，通过后打开打印页
     *
     * @returns false 表示所选日期已不可选，对话框清空日期并提示重新选择
     */
    const handlePrintConfirm = useCallback(async (selectedDate: Dayjs): Promise<boolean> => {
        if (!warehouseId) return true;

        // ★ 在用户点击的同步上下文中先打开窗口（必须在任何 await 之前），避免被浏览器拦截弹窗
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            message.error(t('delivery.routes.message.unableToOpenPrintWindow'));
            return true;
        }
        printWindow.document.write('<p>Loading...</p>');

        // 跨时段校验：按点击确认时的仓库时间复核（如 20:58 选当日、21:01 才确认）
        const whsNow = await fetchFreshWarehouseNow();
        if (!isTaskDateSelectable(selectedDate, whsNow)) {
            printWindow.close();
            return false;
        }
        setPrintModalVisible(false);

        try {
            setPrintLoading(true);
            const taskDateStr = selectedDate.format('YYYY-MM-DD');
            const { data } = await generateBillOfLadingPdf({
                variables: { warehouseId: warehouseId.toString(), taskDate: taskDateStr },
            });
            const pdfData = (data as any)?.[billOfLadingKey];
            if (pdfData) {
                const binaryString = atob(pdfData);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                printWindow.location.href = url;
                printWindow.onload = () => {
                    printWindow.print();
                    setTimeout(() => URL.revokeObjectURL(url), 3000);
                };
            } else {
                printWindow.close();
                message.error(`${t('delivery.routes.message.failedToGenerateBillOfLading')} ${taskDateStr}`);
            }
        } catch (error) {
            printWindow.close();
            console.error('Generate bill of lading failed:', error);
            message.error(t('delivery.routes.message.failedToGenerateBillOfLadingGeneral'));
        } finally {
            setPrintLoading(false);
        }
        return true;
    }, [generateBillOfLadingPdf, warehouseId, fetchFreshWarehouseNow, billOfLadingKey, t]);

    /**
     * 发布按钮点击事件：前端校验 + 数据过期校验通过后打开确认弹窗选择派送日期（R2）。
     * 依赖日期的分拣贴号 / 重发布冲突校验在确认弹窗选定日期后执行（见 handleConfirmPublish）。
     */
    const handlePublishAllClick = useCallback(async () => {
        // 发布前刷新仓库时间，保证确认弹窗日期面板的可选范围准确
        await fetchFreshWarehouseNow();
        // 第一层：校验前端校验分配合理性
        const errors = validateForPublish();
        if (errors.length > 0) {
            setValidationErrors(errors);
            setPublishModalVisible(true);
            return;
        }

        // 第二层：校验数据是否过期
        if (!warehouseId) return;
        const selectedRows = tableData.filter(
            (row: any) => selectedRowKeys.includes(row.key) && row.status !== RowStatus.PUBLISHING,
        );
        const areaIds = selectedRows.map((row: any) => row.areaId).filter(Boolean) as number[];
        const totalParcels = selectedRows.reduce((sum: number, row: any) => sum + (row.totalParcels || 0), 0);
        const areaDspMappings = selectedRows
            .filter((row: any) => row.areaId && row.dspId)
            .map((row: any) => ({ areaId: row.areaId as number, dspId: row.dspId as number }));

        const { data: staleData } = await checkDataStale({
            variables: { warehouseId, areaIds, totalParcels, areaDspMappings },
        });
        const staleResult = staleData?.checkDataStaleByPlanningPool;
        if (staleResult?.parcelsStale || (staleResult?.dspStaleAreas?.length ?? 0) > 0) {
            setDataStaleResult(staleResult ?? null);
            setDataStaleVisible(true);
            return;
        }

        // 通过 → 打开确认弹窗，由用户选择派送日期
        setPublishTaskDate(null);
        setConfirmModalVisible(true);
    }, [
        validateForPublish, tableData, selectedRowKeys, warehouseId,
        checkDataStale, fetchFreshWarehouseNow,
    ]);

    /**
     * 按所选派送日期做重发布冲突校验，通过则进入发布进度弹窗
     */
    const runRepublishConflictCheckAndOpenPublish = useCallback(async (selectedDate: Dayjs, areaIds: number[]) => {
        if (!warehouseId) return;
        const { data: conflictData } = await checkRepublishConflict({
            variables: { taskDate: selectedDate.format('YYYY-MM-DD'), warehouseId, areaIds },
        });
        const conflictResult = conflictData?.checkRepublishConflict;
        if (conflictResult?.hasConflict && conflictResult.scannedOrders.length > 0) {
            setRepublishConflictOrders(conflictResult.scannedOrders);
            setRepublishConflictVisible(true);
            return;
        }
        setValidationErrors([]);
        setPublishModalVisible(true);
    }, [warehouseId, checkRepublishConflict]);

    /**
     * 确认弹窗点「确认」：按最新仓库时间复核所选日期，再按该日期做分拣贴号 / 重发布冲突校验
     *
     * @returns false 表示所选日期已不可选，弹窗清空日期并提示重新选择
     */
    const handleConfirmPublish = useCallback(async (selectedDate: Dayjs): Promise<boolean> => {
        if (!warehouseId) return true;

        // 跨时段校验：按点击确认时的仓库时间复核（如 20:58 选当日、21:01 才确认）
        const whsNow = await fetchFreshWarehouseNow();
        if (!isTaskDateSelectable(selectedDate, whsNow)) {
            return false;
        }
        setPublishTaskDate(selectedDate);
        setConfirmModalVisible(false);

        const selectedRows = tableData.filter(
            (row: any) => selectedRowKeys.includes(row.key) && row.status !== RowStatus.PUBLISHING,
        );
        const areaIds = selectedRows.map((row: any) => row.areaId).filter(Boolean) as number[];

        // 分拣贴号灰度场景"不可发布"区域校验（按所选日期）
        // 当用户使用顶部全选时（已选所有可选行），需要把页面上所有可见区域 id（含灰度禁用行）
        // 一并送去校验，以便给出完整提示；否则仅校验已选区域。
        const triggeredBySelectAll = selectableKeys.length > 0
            && selectedRowKeys.length === selectableKeys.length;
        const areaIdsForUnpublishableCheck = triggeredBySelectAll
            ? Array.from(new Set<number>([
                ...areaIds,
                ...visibleAreaIdsForUnpublishable,
            ]))
            : areaIds;

        if (areaIdsForUnpublishableCheck.length > 0) {
            const { data: unpublishableData } = await fetchUnpublishableAreaIds({
                variables: { taskDate: selectedDate.format('YYYY-MM-DD'), warehouseId, areaIds: areaIdsForUnpublishableCheck },
            });
            const unpublishable = (unpublishableData?.listSortingTagScanUnpublishableAreaIds ?? []).map(Number);
            // 同步刷新页面上的灰度行（保持 UI 与后端一致）
            setUnpublishableAreaIds(unpublishable);
            if (unpublishable.length > 0) {
                const blockedNames = unpublishable.map(id => areaNameMap[id]).filter(Boolean);
                pendingPublishUnpublishableAreaIdsRef.current = unpublishable;
                setUnpublishableModalAreaNames(blockedNames);
                setUnpublishableModalVisible(true);
                return true;
            }
        }

        // 重发布冲突校验（按所选日期）
        await runRepublishConflictCheckAndOpenPublish(selectedDate, areaIds);
        return true;
    }, [
        warehouseId, tableData, selectedRowKeys, fetchFreshWarehouseNow, fetchUnpublishableAreaIds,
        selectableKeys, visibleAreaIdsForUnpublishable, areaNameMap, runRepublishConflictCheckAndOpenPublish,
    ]);

    /**
     * 用户在"不可发布区域"弹窗点击"继续发布"：过滤掉不可发布区域后继续发布流程
     */
    const handleUnpublishableContinue = useCallback(async () => {
        const unpublishableSet = new Set(pendingPublishUnpublishableAreaIdsRef.current);
        pendingPublishUnpublishableAreaIdsRef.current = [];
        setUnpublishableModalVisible(false);
        setUnpublishableModalAreaNames([]);

        // 从已选中行里剔除不可发布的区域
        const filteredKeys = selectedRowKeys.filter((k) => {
            const row = tableData.find((r: any) => r.key === k);
            return row && !unpublishableSet.has(Number(row.areaId));
        });
        if (filteredKeys.length === 0) {
            return;
        }
        setSelectedRowKeys(filteredKeys);

        if (!warehouseId || !publishTaskDate) return;
        const selectedRows = tableData.filter((row: any) => filteredKeys.includes(row.key));
        const areaIds = selectedRows.map((row: any) => row.areaId).filter(Boolean) as number[];

        // 继续走重发布冲突校验（日期已在确认弹窗中选定）
        await runRepublishConflictCheckAndOpenPublish(publishTaskDate, areaIds);
    }, [selectedRowKeys, tableData, warehouseId, publishTaskDate, runRepublishConflictCheckAndOpenPublish]);

    /**
     * 用户在"不可发布区域"弹窗点击"取消"
     */
    const handleUnpublishableCancel = useCallback(() => {
        pendingPublishUnpublishableAreaIdsRef.current = [];
        setUnpublishableModalVisible(false);
        setUnpublishableModalAreaNames([]);
    }, []);

    /**
     * 取消发布进度弹窗
     */
    const handlePublishCancel = useCallback((_shouldStopPolling: boolean = true) => {
        setPublishModalVisible(false);
        setValidationErrors([]);
    }, []);

    /**
     * 构造数据并请求发布接口
     */
    const handleStartPublish = useCallback(async () => {
        // 派送日期必须已在确认弹窗中选定
        if (!warehouseId || !publishTaskDate) return;

        const selectedRows = tableData.filter(
            (row: any) => selectedRowKeys.includes(row.key) && row.status !== RowStatus.PUBLISHING,
        );

        const splitModeToAssignType = (mode: string): AssignType => {
            if (mode === 'map') return AssignType.BY_MAP;
            if (mode === 'zipcode') return AssignType.BY_ZIPCODE;
            if (mode === 'stop') return AssignType.BY_STOP;
            if (mode === 'algo') return AssignType.BY_ALGORITHM;
            return AssignType.DIRECT;
        };

        const resolveDriver = (accountIdStr: string | undefined, dspId: number): { driverId?: number; driverName?: string } => {
            if (!accountIdStr) return {};
            const driver = (driversByDspId[dspId] ?? []).find(d => d.accountId.toString() === accountIdStr);
            if (!driver) return {};
            return { driverId: Number(driver.accountId), driverName: driver.name };
        };

        const taskList: AssignTaskInput[] = selectedRows.map((row: any) => {
            const childSegments = tableData.filter(
                (r: any) => r.isSegment && r.parentRecord?.key === row.key
            );
            const hasSplit = childSegments.length > 1;
            const splitMode = hasSplit
                ? (childSegments[0]?.splitMode as string | undefined)
                : (row.splitMode as string | undefined);

            const task: AssignTaskInput = {
                dspTaskId: row.dspTaskId ?? undefined,
                areaId: row.areaId,
                areaName: row.areaName,
                split: hasSplit,
                dspId: row.dspId,
                dspName: row.dspName,
                assignType: splitMode
                    ? splitModeToAssignType(splitMode)
                    : AssignType.DIRECT,
                driverId: row.driverId,
                driverName: row.driverName,
                parcelCount: row.totalParcels,
                startAddress: row.startAddress ?? undefined,
                endAddress: row.endAddress ?? undefined,
            };

            // 多分段：构建 childTaskList（统一处理缓存子行和 API 子行）
            if (hasSplit && splitMode) {
                task.childTaskList = childSegments.map((seg: any) => {
                    const accountIdStr = subRowDrivers[seg.dspTaskId ?? seg.id] ?? seg.driverAccountId;
                    const { driverId, driverName } = resolveDriver(accountIdStr, row.dspId);
                    const child: AssignTaskInput = {
                        dspTaskId: seg.dspTaskId ?? undefined,
                        areaId: seg.areaId ?? row.areaId,
                        areaName: seg.areaName ?? seg.name,
                        split: false,
                        dspId: seg.dspId ?? row.dspId,
                        dspName: seg.dspName ?? row.dspName,
                        assignType: splitModeToAssignType(splitMode),
                        driverId,
                        driverName,
                        startAddress: seg.startAddress ? (({ __typename, ...r }) => r)(seg.startAddress as any) : null,
                        endAddress: seg.endAddress ? (({ __typename, ...r }) => r)(seg.endAddress as any) : null,
                        orderIds: seg.assignedOrderIds?.length > 0 ? seg.assignedOrderIds : undefined,
                        parcelCount: seg.totalParcels || seg.totalParcelCount || 0,
                    };
                    if (seg.assignTypeDetail?.length > 0) {
                        child.assignTypeDetail = seg.assignTypeDetail;
                    } else if (splitMode === 'map' && seg.polygons?.length > 0) {
                        child.assignTypeDetail = seg.polygons.map((poly: any[]) => poly.map((p: any) => `${p.lat},${p.lng}`).join(';'));
                    } else if (splitMode === 'zipcode' && seg.zipcodes?.length > 0) {
                        child.assignTypeDetail = seg.zipcodes;
                    } else if (splitMode === 'stop' && seg.stopRanges?.length > 0) {
                        child.assignTypeDetail = seg.stopRanges.map((r: any) => `${r.parcelNumberStart},${r.parcelNumberEnd}`);
                    } else if (splitMode === 'algo') {
                        child.assignTypeDetail = [`${seg.capacityMin ?? 0},${seg.capacityMax ?? 0}`, ...(seg.preferredZipcodes ?? [])];
                    }
                    if (splitMode === 'algo' && seg.algoRecordId) {
                        child.algoRecordId = seg.algoRecordId;
                    }
                    return child;
                });
            }

            return task;
        });
        setIsPublishing(true);
        try {
            await assignDrivers({
                variables: {
                    request: {
                        warehouseId,
                        taskDate: publishTaskDate.format('YYYY-MM-DD'),
                        taskList,
                    },
                },
            });
        } finally {
            setIsPublishing(false);
        }

        // 发布成功后清除已发布行的缓存，下次进抽屉读后端最新数据
        if (warehouseId) {
            selectedRows.forEach((row: any) => {
                if (row.areaId) deleteSplitRouteCache(warehouseId, Number(row.areaId));
            });
        }
        setPublishModalVisible(false);
        // 发布成功提示：行级 PUBLISHING 由 routeStatus 驱动，无需额外置全局状态
        message.info(t('delivery.routes.message.publishTaskStarted'));
        setSelectedRowKeys([]);
        setDriverAssignments({});
        await refetchPendingRoutes();
        await refetchSummary();
    }, [warehouseId, tableData, selectedRowKeys, publishTaskDate, splitConfigs, subRowDrivers, driversByDspId, assignDrivers, refetchPendingRoutes, refetchSummary, t]);

    return (
        <div style={{padding: '24px'}}>

            {/* 页面头部 */}
            <Row justify="space-between" align="middle" className="mb-3">
                <Col>
                    <div className="flex items-center space-x-4">
                        <div className="border-l-4 border-indigo-600 pl-2 text-xl font-medium">
                            {t('delivery.routes.label.taskPlanning')}
                        </div>
                    </div>
                    {(() => {
                        if (selectedRowKeys.length > 0) {
                            const selectedRows = tableData.filter((r: any) => selectedRowKeys.includes(r.key));
                            const selectedParcelCount = selectedRows.reduce((sum: number, r: any) => sum + (r.totalParcels || 0), 0);
                            return (
                                <div className="mt-2">
                                    <Text type="secondary" className="text-xs">
                                        {t('delivery.routes.message.selectedAreasAndParcels', {
                                            areaCount: selectedRowKeys.length,
                                            parcelCount: selectedParcelCount,
                                        })}
                                    </Text>
                                </div>
                            );
                        }
                        const areaRows = tableData.filter((r: any) => !r.isSegment);
                        const totalCount = areaRows.length;
                        const totalParcels = areaRows.reduce((sum: number, r: any) => sum + (r.totalParcels || 0), 0);
                        return totalCount > 0 ? (
                            <div className="mt-2">
                                <Text type="secondary" className="text-xs">
                                    {t('delivery.routes.message.totalAreasAndParcels', {
                                        areaCount: totalCount,
                                        parcelCount: totalParcels,
                                    })}
                                </Text>
                            </div>
                        ) : null;
                    })()}
                </Col>
                <Col>
                    <div className="flex items-center gap-4">
                        <div className="flex gap-4">
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined/>}
                                onClick={handlePublishAllClick}
                                disabled={tableData.filter((r: any) => !r.isSegment).length === 0 || selectedRowKeys.length === 0}
                                loading={isPublishing || republishChecking}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold whitespace-nowrap">
                                    {selectedRowKeys.length > 0
                                        ? t('delivery.routes.label.publishSelected', { count: selectedRowKeys.length })
                                        : t('delivery.routes.label.publishAll')
                                    }
                                </span>
                            </Button>
                            <Button
                                type="default"
                                icon={<PrinterOutlined/>}
                                onClick={handlePrintClick}
                                loading={printLoading}
                                disabled={!warehouseId}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold whitespace-nowrap">
                                    {t('delivery.routes.label.print')}
                                </span>
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* 派送任务待发布状态提示 */}
            {(() => {
                const unpublishedCount = tableData.filter(
                    (row: any) => !row.isSegment && row.totalParcels > 0 && Number(row.dspTaskId) === 0
                ).length;
                if (unpublishedCount === 0) return null;
                // R3：页面不再有任务日期，文案统一为「派送任务」，统计口径不变
                const label = t('delivery.routes.message.deliveryTask');
                return (
                    <div className="mb-4 flex flex-col items-end gap-1">
                        <span style={{ fontSize: 14, color: '#595959' }}>
                            {t('delivery.routes.message.publishStatusPending', { label, count: unpublishedCount })}
                        </span>
                    </div>
                );
            })()}

            {/* 待规划包裹和发布成功统计卡片 */}
            <Row gutter={16} className="mb-4" align="stretch">
                <Col span={12}>
                    <Card
                        className="bg-indigo-50 rounded shadow-md transition-all duration-300 relative h-full flex flex-col hover:shadow-lg"
                        bodyStyle={{
                            padding: '20px',
                            textAlign: 'center',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                    >
                        <Spin spinning={summaryLoading}>
                            <div>
                                <div className="text-sm text-gray-600 mb-3">
                                    {t('delivery.routes.label.pendingPlanningParcels')}
                                </div>
                                <button
                                    type="button"
                                    className="text-3xl text-indigo-600 font-bold mb-3 underline decoration-indigo-600 bg-transparent border-0 cursor-pointer p-0 font-inherit w-full hover:text-indigo-700"
                                    onClick={() => openPoolDrawer('all')}
                                >
                                    {pendingPlanning}
                                </button>
                                <div className="flex flex-row justify-center items-center gap-3 mt-2">
                                    <button
                                        type="button"
                                        className="text-sm text-indigo-500 underline decoration-indigo-500 bg-transparent border-0 cursor-pointer p-0 font-inherit hover:text-indigo-700"
                                        onClick={() => openPoolDrawer('new')}
                                    >
                                        {newToWarehouse}
                                        {t('delivery.routes.label.newArrived')}
                                    </button>
                                    <span className="text-sm text-indigo-500">+</span>
                                    <button
                                        type="button"
                                        className="text-sm text-indigo-500 underline decoration-indigo-500 bg-transparent border-0 cursor-pointer p-0 font-inherit hover:text-indigo-700"
                                        onClick={() => openPoolDrawer('returned')}
                                    >
                                        {returnRedispatch}
                                        {t('delivery.routes.label.returnedForRedispatch')}
                                    </button>
                                </div>
                            </div>
                        </Spin>
                    </Card>
                </Col>
                <Col span={12}>
                    <Card
                        className="bg-white rounded shadow-md h-full flex flex-col"
                        bodyStyle={{
                            padding: '20px',
                            textAlign: 'center',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}
                    >
                        <div>
                            <div className="text-sm text-gray-600 mb-2">
                                {t('delivery.routes.label.publishedSuccessfully')}
                                <Tooltip title={t('delivery.routes.label.publishedSuccessfullyTooltip')}>
                                    <QuestionCircleOutlined className="ml-2 text-indigo-500 cursor-help text-base"/>
                                </Tooltip>
                            </div>
                            <Spin spinning={summaryLoading}>
                                <div className="text-3xl text-indigo-700 font-bold">{published}</div>
                            </Spin>
                        </div>
                    </Card>
                </Col>
            </Row>

            {negativeDriverSummary && (
                <Alert
                    type="error"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    className="mb-4"
                    style={{ padding: '16px 24px' }}
                    message={
                        <span className="text-base font-bold" style={{ color: '#cf1322' }}>
                            {t('delivery.routes.message.autoRouteUnassignedDriver', {
                                routes: negativeDriverSummary.routes.join('、'),
                                count: negativeDriverSummary.count,
                            })}
                        </span>
                    }
                />
            )}

            {/* 区域规划表格 */}
            <Card style={{ marginBottom: 24 }}>
                <Table
                    columns={[
                        {
                            title: (
                                <Checkbox
                                    indeterminate={isIndeterminate}
                                    checked={isAllSelected}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                />
                            ),
                            key: 'select',
                            width: 50,
                            render: (_: any, record: any) => {
                                if (record.isSegment) return null;
                                const isUnpublishable = unpublishableAreaIdSet.has(Number(record.areaId));
                                const isAutoRouteGray = autoRouteGrayAreaIdSet.has(Number(record.areaId));
                                const canSelect = record.totalParcels > 0
                                    && record.status !== RowStatus.PUBLISHING
                                    && !isUnpublishable
                                    && !isAutoRouteGray;
                                const checkbox = (
                                    <Checkbox
                                        checked={selectedRowKeys.includes(record.key)}
                                        disabled={!canSelect}
                                        onChange={(e) => handleRowSelect(record.key, e.target.checked)}
                                    />
                                );
                                if (isUnpublishable) {
                                    return (
                                        <Tooltip title={t('delivery.routes.message.sortingTagScanUnpublishableTooltip')}>
                                            <span>{checkbox}</span>
                                        </Tooltip>
                                    );
                                }
                                return checkbox;
                            },
                        },
                        {
                            title: t('delivery.routes.label.area'),
                            key: 'area',
                            width: 190,
                            render: (_, record: any) => {
                                if (record.isSegment) {
                                    return (
                                        <div style={{ paddingLeft: 16 }}>
                                            <Text type="secondary">{record.name}</Text>
                                        </div>
                                    );
                                }
                                const statusConfig: Record<RowStatus, { color?: string; label: string; tip: string; style?: React.CSSProperties }> = {
                                    [RowStatus.EDITED]: { color: 'blue', label: t('splitOrder.page.statusEdited'), tip: t('splitOrder.page.statusEditedTip') },
                                    [RowStatus.PUBLISHED]: { color: 'green', label: t('splitOrder.page.statusPublished'), tip: t('splitOrder.page.statusPublishedTip') },
                                    [RowStatus.PENDING]: { color: 'default', label: t('splitOrder.page.statusPending'), tip: t('splitOrder.page.statusPendingTip') },
                                    [RowStatus.PUBLISHING]: {
                                        label: t('splitOrder.page.statusPublishing'),
                                        tip: t('splitOrder.page.statusPublishingTip'),
                                        // 浅黄色背景 + 黄字（无外边框，与其他状态保持一致）
                                        style: { color: '#d48806', background: '#fffbe6', border: 'none' },
                                    },
                                    [RowStatus.PUBLISH_FAILED]: {
                                        label: t('splitOrder.page.statusPublishFailed'),
                                        tip: t('splitOrder.page.statusPublishFailedTip'),
                                        // 浅红色背景 + 红字（无外边框，与其他状态保持一致）
                                        style: { color: '#cf1322', background: '#fff1f0', border: 'none' },
                                    },
                                };
                                const cfg = statusConfig[record.status as RowStatus]
                                    ?? { color: 'default', label: t('splitOrder.page.statusPending'), tip: '' };
                                const statusTag = (
                                    <Tag
                                        bordered={false}
                                        color={cfg.color}
                                        style={{ marginRight: 6, minWidth: 110, textAlign: 'center', ...(cfg.style ?? {}) }}
                                    >
                                        {record.status === RowStatus.PUBLISHING && (
                                            <LoadingOutlined spin style={{ marginRight: 4 }} />
                                        )}
                                        {cfg.label}
                                        <Tooltip title={cfg.tip}>
                                            <QuestionCircleOutlined style={{ marginLeft: 4, fontSize: 11, cursor: 'help' }} />
                                        </Tooltip>
                                    </Tag>
                                );
                                return <div className="flex items-center">{statusTag}<Text strong>{record.areaName || '-'}</Text></div>;
                            }
                        },
                        {
                            title: t('delivery.routes.label.dsp'),
                            key: 'dsp',
                            width: 150,
                            render: (_, record: any) => <Text type={record.isSegment ? 'secondary' : undefined}>{record.dspName || '-'}</Text>
                        },
                        {
                            title: t('delivery.routes.label.new'),
                            key: 'new',
                            width: 80,
                            align: 'left' as const,
                            render: (_, record: any) => {
                                if (record.isSegment) return null;
                                return <Text style={{color: record.newParcels > 0 ? '#ff4d4f' : '#666'}}>{record.newParcels ?? 0}</Text>;
                            }
                        },
                        {
                            title: t('delivery.routes.label.planned'),
                            key: 'planned',
                            width: 80,
                            align: 'left' as const,
                            render: (_, record: any) => {
                                if (record.isSegment) return null;
                                return <Text style={{color: record.plannedParcels > 0 ? '#52c41a' : '#666'}}>{record.plannedParcels ?? 0}</Text>;
                            }
                        },
                        {
                            title: t('delivery.routes.label.areaTotal'),
                            key: 'areaTotal',
                            width: 320,
                            align: 'left' as const,
                            render: (_, record: any) => {
                                if (record.isSegment) return renderSplitInfo(record);
                                return <Text strong style={{color: '#4f46e5'}}>{record.totalParcels ?? 0}</Text>;
                            }
                        },
                        {
                            title: t('delivery.routes.label.driver'),
                            key: 'driver',
                            width: 320,
                            render: (_: any, record: any) => {
                                const activeDrivers = (driversByDspId[record.dspId] || []).filter(d => d.isEmployed);
                                const driverOptionItems = activeDrivers.map((driver) => {
                                    const isVerified = driver.verificationStatus === 2;
                                    const isBlacklisted = !!driver.isBlacklist;
                                    return (
                                        <Select.Option key={driver.accountId} value={driver.accountId.toString()} disabled={isBlacklisted || !isVerified}>
                                            <span style={{fontSize: 14}}>{driver.phone}</span>
                                            <span style={{fontSize: 12, color: '#666', marginLeft: 4}}>({driver.name})</span>
                                            {isBlacklisted
                                                ? <span style={{fontSize: 12, color: '#faad14', marginLeft: 8}}>{t('delivery.routes.label.driverBlacklistedTag')}</span>
                                                : isVerified
                                                    ? <span style={{fontSize: 12, color: '#52c41a', marginLeft: 8}}>{t('delivery.routes.label.driverVerified')}</span>
                                                    : <span style={{fontSize: 12, color: '#faad14', marginLeft: 8}}>{t('delivery.routes.label.driverUnverifiedShort')}</span>
                                            }
                                        </Select.Option>
                                    );
                                });

                                // 不可发布区域：禁用司机选择
                                const parentAreaIdForSegment = record.isSegment ? Number(record.parentRecord?.areaId) : null;
                                const isUnpublishable = record.isSegment
                                    ? (parentAreaIdForSegment != null && unpublishableAreaIdSet.has(parentAreaIdForSegment))
                                    : unpublishableAreaIdSet.has(Number(record.areaId));
                                const isAutoRouteGray = record.isSegment
                                    ? autoRouteGrayAreaIdSet.has(Number(record.parentRecord?.areaId))
                                    : autoRouteGrayAreaIdSet.has(Number(record.areaId));
                                if (isAutoRouteGray) {
                                    if (!record.isSegment && record.split) return <>-</>;
                                    const driverDisplay = record.driverName || (record.driverAccountId
                                        ? activeDrivers.find(d => d.accountId.toString() === record.driverAccountId)?.name
                                        : undefined);
                                    const isNegativeDriver = record.driverId != null && Number(record.driverId) < 0;
                                    const driverPhone = !isNegativeDriver && record.driverAccountId
                                        ? activeDrivers.find(d => d.accountId.toString() === record.driverAccountId)?.phone
                                        : undefined;
                                    const searchText = grayDriverSearchText[record.id] || '';
                                    const filteredGrayDrivers = activeDrivers.filter(d => {
                                        if (!searchText) return true;
                                        const lower = searchText.toLowerCase();
                                        return d.name.toLowerCase().includes(lower) || (d.phone || '').toLowerCase().includes(lower);
                                    });
                                    const driverMenu = (
                                        <div className="w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
                                            <div className="p-2 border-b">
                                                <input
                                                    type="text"
                                                    placeholder={t('delivery.routes.label.searchPlaceholder') as string}
                                                    value={searchText}
                                                    onChange={(e) => setGrayDriverSearchText(prev => ({ ...prev, [record.id]: e.target.value }))}
                                                    className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                            <div className="p-3">
                                                <Radio.Group
                                                    value={record.driverAccountId || undefined}
                                                    onChange={async (e) => {
                                                        const selectedAccountId = e.target.value;
                                                        const selectedDriver = activeDrivers.find(d => d.accountId.toString() === selectedAccountId);
                                                        setGrayDriverDropdownVisible(prev => ({ ...prev, [record.id]: false }));
                                                        setGrayDriverSearchText(prev => ({ ...prev, [record.id]: '' }));
                                                        const hideLoading = message.loading(t('delivery.tasks.message.changingDriver'), 0);
                                                        try {
                                                            await changeAutoRouteDriver({
                                                                variables: {
                                                                    driverTaskId: record.driverTaskId,
                                                                    driverAccountId: Number(selectedAccountId),
                                                                    driverName: selectedDriver?.name || '',
                                                                },
                                                                refetchQueries: [pendingRoutesQuery],
                                                            });
                                                            hideLoading();
                                                            message.success(t('delivery.tasks.message.changeDriverSuccess'));
                                                            refetchPendingRoutes();
                                                        } catch (err: any) {
                                                            hideLoading();
                                                            message.error(err?.message || t('delivery.tasks.message.changeDriverFailed'));
                                                        }
                                                    }}
                                                    className="w-full"
                                                >
                                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                                        {filteredGrayDrivers.length > 0 ? filteredGrayDrivers.map(driver => {
                                                            const isVerified = driver.verificationStatus === 2;
                                                            const isBlacklisted = !!driver.isBlacklist;
                                                            return (
                                                                <div key={driver.accountId} className="flex items-center">
                                                                    <Radio
                                                                        value={driver.accountId.toString()}
                                                                        disabled={isBlacklisted || !isVerified}
                                                                    >
                                                                        <span className="font-medium">{driver.name}</span>
                                                                        {isBlacklisted
                                                                            ? <span style={{fontSize: 12, color: '#faad14', marginLeft: 8}}>{t('delivery.routes.label.driverBlacklistedTag')}</span>
                                                                            : isVerified
                                                                                ? <span style={{fontSize: 12, color: '#52c41a', marginLeft: 8}}>{t('delivery.routes.label.driverVerified')}</span>
                                                                                : <span style={{fontSize: 12, color: '#faad14', marginLeft: 8}}>{t('delivery.routes.label.driverUnverifiedShort')}</span>
                                                                        }
                                                                    </Radio>
                                                                </div>
                                                            );
                                                        }) : (
                                                            <div className="text-gray-400 text-center py-2">{t('delivery.tasks.label.noMatchingDrivers')}</div>
                                                        )}
                                                    </div>
                                                </Radio.Group>
                                            </div>
                                        </div>
                                    );
                                    return (
                                        <div className="flex items-center gap-2">
                                            <div>
                                                <div className={isNegativeDriver ? 'text-red-500' : ''}>{isNegativeDriver ? t('delivery.tasks.label.unassignedDriver') : (driverDisplay || '-')}</div>
                                                {!isNegativeDriver && driverPhone && (
                                                    <div className="text-xs text-gray-400">{driverPhone}</div>
                                                )}
                                            </div>
                                            {isNegativeDriver && (
                                                <Dropdown
                                                    overlay={driverMenu}
                                                    trigger={['click']}
                                                    visible={grayDriverDropdownVisible[record.id]}
                                                    onVisibleChange={(visible) => {
                                                        setGrayDriverDropdownVisible(prev => ({ ...prev, [record.id]: visible }));
                                                        if (!visible) setGrayDriverSearchText(prev => ({ ...prev, [record.id]: '' }));
                                                    }}
                                                    placement="bottomRight"
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<SwapOutlined />}
                                                        className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                                                        style={{padding: '0 4px', height: 24, minWidth: 24}}
                                                    />
                                                </Dropdown>
                                            )}
                                        </div>
                                    );
                                }

                                if (isUnpublishable) {
                                    const currentValue = record.isSegment
                                        ? (record.driverAccountId || undefined)
                                        : (record.driverId ? String(record.driverId) : undefined);
                                    return (
                                        <Tooltip title={t('delivery.routes.message.sortingTagScanUnpublishableTooltip')}>
                                            <Select
                                                style={{width: 320}}
                                                placeholder={t('delivery.routes.label.searchPlaceholder')}
                                                value={currentValue}
                                                disabled
                                            >
                                                {driverOptionItems}
                                            </Select>
                                        </Tooltip>
                                    );
                                }

                                if (record.isSegment) {
                                    // 发布中 / 已发布状态子行：禁止修改司机
                                    if (record.status === RowStatus.PUBLISHING) {
                                        return (
                                            <Select
                                                style={{width: 320}}
                                                value={record.driverAccountId || undefined}
                                                disabled
                                            >
                                                {driverOptionItems}
                                            </Select>
                                        );
                                    }
                                    const savedDriverId = record.id in subRowDrivers
                                        ? (subRowDrivers[record.id] || undefined)
                                        : (record.driverAccountId || undefined);
                                    const needsExclusion = record.splitMode !== 'map';
                                    const usedByOthers = needsExclusion ? new Set(
                                        tableData
                                            .filter((r: any) => r.isSegment && r.id !== record.id && r.parentRecord?.key === record.parentRecord?.key)
                                            .map((r: any) => subRowDrivers[r.id] ?? r.driverAccountId)
                                            .filter(Boolean)
                                    ) : new Set<string>();
                                    const segmentDriverOptions = driverOptionItems.map((opt: any) => {
                                        const isUsedByOther = usedByOthers.has(opt.props.value) && opt.props.value !== savedDriverId;
                                        if (!isUsedByOther) return opt;
                                        return React.cloneElement(opt, {
                                            disabled: true,
                                            children: [
                                                <span key="assigned" style={{ fontSize: 12, color: '#999', marginRight: 8 }}>{t('splitOrder.common.assigned')}</span>,
                                                ...React.Children.toArray(opt.props.children),
                                            ],
                                        });
                                    });
                                    return (
                                        <Select
                                            style={{width: 320}}
                                            placeholder={t('delivery.routes.label.searchPlaceholder')}
                                            value={savedDriverId || undefined}
                                            allowClear
                                            showSearch
                                            filterOption={(input, option) => {
                                                const driver = activeDrivers.find(d => d.accountId.toString() === option?.value);
                                                if (!driver) return false;
                                                const text = input.toLowerCase();
                                                return driver.phone.includes(text) || driver.name.toLowerCase().includes(text);
                                            }}
                                            onChange={async (val) => {
                                                const parentAreaId = record.parentRecord?.areaId;
                                                const parentDspTaskId = record.parentRecord?.dspTaskId;
                                                const wId = record.parentRecord?.warehouseId ?? warehouseId;
                                                let parsed = (wId && parentAreaId) ? await loadSplitRouteCache(wId, Number(parentAreaId)) : null;
                                                // 缓存不存在（已发布后被清除）：从后端数据构建
                                                if (!parsed && parentAreaId && wId) {
                                                    const route = (pendingRoutesData?.getPendingRoutes ?? []).find((r: any) => String(r.areaId) === String(parentAreaId));
                                                    const children = route?.childTaskList ?? [];
                                                    const assignType = children[0]?.assignType ?? null;
                                                    if (assignType != null && children.length > 0) {
                                                        try {
                                                            // 查询当前实时订单列表，用于匹配 assignedOrderIds
                                                            const { data: routeOrdersData } = await fetchRouteOrders({
                                                                variables: { request: { warehouseId: Number(wId), areaId: Number(parentAreaId) } },
                                                            });
                                                            let orderList = routeOrdersData?.getRouteOrders?.orderList ?? [];

                                                            // stop 模式：预规划未完成时 stopNumber 为空，需调 fastRoutePlanning 获取
                                                            if (assignType === AssignType.BY_STOP && !routeOrdersData?.getRouteOrders?.prePlanningCompleted) {
                                                                const { data: fastPlanData } = await fetchFastRoutePlanning({
                                                                    variables: { warehouseId: Number(wId), areaId: Number(parentAreaId) },
                                                                });
                                                                const fastPlanResult = (fastPlanData as any)?.[fastRoutePlanningKey];
                                                                if (fastPlanResult?.length) {
                                                                    orderList = fastPlanResult;
                                                                }
                                                            }

                                                            // 统一走 fetchTaskDetail 获取完整子任务数据（含 startAddress/endAddress/routePlanningOrderList）
                                                            let buildChildren: any[] = children;
                                                            if (parentDspTaskId && warehouseId) {
                                                                const { data: detailData } = await fetchTaskDetail({
                                                                    variables: { request: { warehouseId, dspTaskId: Number(parentDspTaskId) } },
                                                                });
                                                                const detail = detailData?.getDspTaskDetailWithChildren;
                                                                const detailChildren = detail?.childTaskList?.length ? detail.childTaskList : detail ? [detail] : [];
                                                                if (detailChildren.length > 0) {
                                                                    buildChildren = detailChildren;
                                                                }
                                                            }
                                                            parsed = buildCacheFromApiChildren(assignType, buildChildren, route?.areaName ?? 'Area', parentAreaId, orderList);
                                                        } catch (e) {
                                                            console.error('[SplitOrderPage] fetch route orders or task detail for cache build failed:', e);
                                                        }
                                                    }
                                                }
                                                if (parsed) {
                                                    const modes = ['map', 'zipcode', 'stop'] as const;
                                                    modes.forEach(mode => {
                                                        if (Array.isArray(parsed![mode])) {
                                                            parsed![mode] = parsed![mode].map((s: any) =>
                                                                s.id === record.id ? { ...s, driverAccountId: val } : s
                                                            );
                                                        }
                                                    });
                                                    if (parsed.algo?.segments?.length) {
                                                        parsed.algo.segments = parsed.algo.segments.map((s: any) =>
                                                            s.id === record.id ? { ...s, driverAccountId: val } : s
                                                        );
                                                    }
                                                    if (wId && parentAreaId) await saveSplitRouteCache(wId, Number(parentAreaId), parsed);
                                                    setSubRowDrivers(prev => ({ ...prev, [record.id]: val ?? null }));
                                                    await refreshSplitConfigs();
                                                } else {
                                                    // 无法构建缓存时仍更新 subRowDrivers
                                                    setSubRowDrivers(prev => ({ ...prev, [record.id]: val ?? null }));
                                                }
                                            }}
                                        >
                                            {segmentDriverOptions}
                                        </Select>
                                    );
                                }

                                // 有实际子行时父级不显示司机选择框
                                const hasRealSegments = tableData.some((r: any) => r.isSegment && r.parentRecord?.key === record.key);
                                if (hasRealSegments) return <span className="text-gray-300">—</span>;

                                // 发布中：禁止修改司机
                                if (record.status === RowStatus.PUBLISHING) {
                                    return (
                                        <Select
                                            style={{width: 320}}
                                            placeholder={t('delivery.routes.label.searchPlaceholder')}
                                            value={record.driverId ? String(record.driverId) : undefined}
                                            disabled
                                        >
                                            {driverOptionItems}
                                        </Select>
                                    );
                                }

                                return (
                                    <Select
                                        style={{width: 320}}
                                        placeholder={t('delivery.routes.label.searchPlaceholder')}
                                        value={record.driverId ? String(record.driverId) : undefined}
                                        loading={driversLoading}
                                        allowClear
                                        showSearch
                                        filterOption={(input, option) => {
                                            const driver = activeDrivers.find(d => d.accountId.toString() === option?.value);
                                            if (!driver) return false;
                                            const text = input.toLowerCase();
                                            return driver.phone.includes(text) || driver.name.toLowerCase().includes(text);
                                        }}
                                        onChange={async (val) => {
                                            handleDriverChange(record.key, record.dspId, val);
                                            // 有缓存时同步司机（单分段走直接指派，不需要缓存）
                                            if (warehouseId && record.areaId) {
                                                const parsed = await loadSplitRouteCache(warehouseId, Number(record.areaId));
                                                if (parsed) {
                                                    const modes = ['map', 'zipcode', 'stop'] as const;
                                                    modes.forEach(mode => {
                                                        if (Array.isArray(parsed[mode])) {
                                                            parsed[mode] = parsed[mode].map((s: any) => ({ ...s, driverAccountId: val }));
                                                        }
                                                    });
                                                    if (parsed.algo?.segments?.length) {
                                                        parsed.algo.segments = parsed.algo.segments.map((s: any) => ({ ...s, driverAccountId: val }));
                                                    }
                                                    await saveSplitRouteCache(warehouseId, Number(record.areaId), parsed);
                                                }
                                            }
                                        }}
                                    >
                                        {driverOptionItems}
                                    </Select>
                                );
                            },
                        },
                        {
                            title: t('delivery.routes.label.action'),
                            key: 'action',
                            width: 120,
                            render: (_: any, record: any) => {
                                if (record.isSegment) return null;
                                const buttonLabel = record.status === RowStatus.PENDING
                                    ? t('splitOrder.page.splitRoute')
                                    : t('splitOrder.page.adjustRoute');
                                const isUnpublishable = unpublishableAreaIdSet.has(Number(record.areaId));
                                const isAutoRouteGray = autoRouteGrayAreaIdSet.has(Number(record.areaId));
                                const actionDisabled = isPublishing || record.status === RowStatus.PUBLISHING || isUnpublishable;
                                const splitButton = isAutoRouteGray ? null : (
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<EditOutlined/>}
                                        disabled={!record.totalParcels || actionDisabled}
                                        style={{ color: (record.totalParcels && !actionDisabled) ? '#4f46e5' : undefined }}
                                        onClick={() => {
                                            setSplitRouteRecord(record as SplitRouteRecord);
                                            setSplitRouteDrawerVisible(true);
                                        }}
                                    >
                                        {buttonLabel}
                                    </Button>
                                );
                                return (
                                    <Space direction="vertical" size={6}>
                                        {isUnpublishable && splitButton ? (
                                            <Tooltip title={t('delivery.routes.message.sortingTagScanUnpublishableTooltip')}>
                                                <span>{splitButton}</span>
                                            </Tooltip>
                                        ) : splitButton}
                                        {record.status !== RowStatus.PENDING && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<EyeOutlined />}
                                                disabled={isPublishing || record.status === RowStatus.PUBLISHING}
                                                style={{ color: (isPublishing || record.status === RowStatus.PUBLISHING) ? undefined : '#4f46e5' }}
                                                onClick={() => openHistoryDrawer(record.dspTaskId ?? null, Number(record.areaId) || null, warehouseId)}
                                            >
                                                {t('splitOrder.page.viewResult')}
                                            </Button>
                                        )}
                                        {isAutoRouteGray && (
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<AppstoreOutlined />}
                                                style={{ color: '#4f46e5' }}
                                                onClick={() => {
                                                    setTemplateRecord(record as SplitRouteRecord);
                                                    setTemplateDrawerVisible(true);
                                                }}
                                            >
                                                {t('splitOrder.template.setRouteTemplate')}
                                            </Button>
                                        )}
                                    </Space>
                                );
                            },
                        },
                    ]}
                    dataSource={tableData}
                    loading={pendingRoutesLoading || !autoRouteGrayReady}
                    rowKey="key"
                    pagination={false}
                    size="small"
                    rowClassName={(record: any) => {
                        const checkAreaId = record.isSegment
                            ? Number(record.parentRecord?.areaId)
                            : Number(record.areaId);
                        if (autoRouteGrayAreaIdSet.has(checkAreaId)) {
                            if (record.driverId != null && Number(record.driverId) < 0) {
                                return 'bg-red-100';
                            }
                            return '';
                        }
                        if (unpublishableAreaIdSet.has(checkAreaId)) {
                            return 'bg-gray-100 opacity-60';
                        }
                        const classes: string[] = [];
                        if (record.isSegment) classes.push('bg-gray-50');
                        if (record.status === RowStatus.PUBLISHING) classes.push('opacity-60');
                        return classes.join(' ');
                    }}
                />
            </Card>

            <SplitOrderWaybillPoolDrawer
                visible={poolDrawerVisible}
                onClose={() => setPoolDrawerVisible(false)}
                initialTab={poolDrawerTab}
                mode={mode}
            />

            {/* 发布确认弹窗 */}
            <SplitOrderPublishConfirmModal
                visible={confirmModalVisible}
                onCancel={() => setConfirmModalVisible(false)}
                onConfirm={handleConfirmPublish}
                warehouseNow={warehouseNow}
                tableData={tableData}
                selectedRowKeys={selectedRowKeys}
            />

            {/* 异步发布进度弹窗（含校验错误展示） */}
            <SplitOrderPublishAllModal
                visible={publishModalVisible && !!warehouseId}
                onCancel={handlePublishCancel}
                selectedTaskDate={publishTaskDate}
                warehouseId={warehouseId!}
                tableData={tableData}
                onStartPublish={handleStartPublish}
                isAsyncPublishing={false}
                asyncPublishProgress={undefined}
                validationErrors={validationErrors}
            />

            {/* 数据过期弹窗 */}
            <DataStaleModal
                visible={dataStaleVisible}
                parcelsStale={dataStaleResult?.parcelsStale}
                dspStaleAreas={dataStaleResult?.dspStaleAreas}
                areaNameMap={areaNameMap}
                dspNameMap={dspNameMap}
                onRefresh={() => {
                    setDataStaleVisible(false);
                    setDataStaleResult(null);
                    void refetchPendingRoutes();
                    void refetchSummary();
                }}
            />

            {/* 拆分线路抽屉 */}
            <SplitRouteDrawer
                visible={splitRouteDrawerVisible}
                record={splitRouteRecord}
                warehouseId={warehouseId}
                warehouseToday={warehouseToday.format('YYYY-MM-DD')}
                warehouseCurrentTime={entryConfigData?.config?.warehouseCurrentTime}
                fetchWarehouseNow={fetchFreshWarehouseNow}
                driversByDspId={driversByDspId}
                allowedModes={availableModes}
                mode={mode}
                onClose={() => {
                    setSplitRouteDrawerVisible(false);
                    setSubRowDrivers({});
                    // 发布走 onPublishSuccess，不重复 refreshSplitConfigs
                    if (!publishFromDrawerRef.current) {
                        refreshSplitConfigs();
                    }
                    publishFromDrawerRef.current = false;
                }}
                onSave={() => {
                    // 保存后自动勾选该区域
                    if (splitRouteRecord) {
                        const key = `${splitRouteRecord.areaId}-${splitRouteRecord.dspTaskId ?? 0}`;
                        setSelectedRowKeys(prev => prev.includes(key) ? prev : [...prev, key]);
                    }
                    setSubRowDrivers({});
                    refreshSplitConfigs();
                }}
                onDataStaleRefresh={() => {
                    void refetchPendingRoutes();
                    void refetchSummary();
                    refreshSplitConfigs();
                }}
                onPublishSuccess={async () => {
                    publishFromDrawerRef.current = true;
                    // 发布成功提示：行级 PUBLISHING 由 routeStatus 驱动，无需额外置全局状态
                    message.info(t('delivery.routes.message.publishTaskStarted'));
                    await refetchPendingRoutes();
                    await refetchSummary();
                    refreshSplitConfigs();
                }}
            />

            {/* 线路模板管理 */}
            <RoutePlanningTemplate
                visible={templateDrawerVisible}
                record={templateRecord}
                warehouseId={warehouseId}
                driversByDspId={driversByDspId}
                allowedModes={availableModes}
                onClose={() => setTemplateDrawerVisible(false)}
            />

            {/* 查看上次分配历史 */}
            <SplitRouteHistoryDrawer
                visible={historyDrawerVisible}
                dspTaskId={historyDspTaskId}
                warehouseId={historyWarehouseId ?? warehouseId}
                areaId={historyAreaId}
                mode={mode}
                onClose={closeHistoryDrawer}
            />

            {/* 二次发布冲突弹窗 */}
            <RepublishConflictModal
                visible={republishConflictVisible}
                orders={republishConflictOrders}
                onStillPublish={() => {
                    // 派送日期已在确认弹窗中选定，直接进入发布
                    setRepublishConflictVisible(false);
                    setValidationErrors([]);
                    setPublishModalVisible(true);
                }}
                onCancel={() => setRepublishConflictVisible(false)}
            />

            {/* 打印前选择任务日期（R4） */}
            <PrintTaskDateModal
                visible={printModalVisible}
                onCancel={() => setPrintModalVisible(false)}
                onConfirm={handlePrintConfirm}
                warehouseNow={warehouseNow}
            />

            {/* 分拣贴号灰度场景：不可发布区域提示弹窗 */}
            <SortingTagScanUnpublishableModal
                visible={unpublishableModalVisible}
                areaNames={unpublishableModalAreaNames}
                onContinue={handleUnpublishableContinue}
                onCancel={handleUnpublishableCancel}
            />
        </div>
    );
};

export default SplitOrderPage;